import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { 
  ArrowRight, 
  Calendar, 
  Users, 
  Sparkles, 
  Heart, 
  Mountain, 
  Sun, 
  Snowflake,
  Compass,
  Star,
  Camera,
  MapPin
} from 'lucide-react';

const InspirationPage = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');

  const inspirationCategories = [
    { id: 'all', name: 'Toutes les inspirations', icon: Compass },
    { id: 'seasonal', name: 'Saisons', icon: Calendar },
    { id: 'family', name: 'En famille', icon: Users },
    { id: 'romantic', name: 'Romantique', icon: Heart },
    { id: 'adventure', name: 'Aventure', icon: Mountain },
    { id: 'wellness', name: 'Bien-être', icon: Sparkles }
  ];

  const inspirationCollections = [
    {
      id: 'winter-luxury',
      category: 'seasonal',
      title: 'Évasions Hivernales de Luxe',
      subtitle: 'Chalets d\'exception sous la neige',
      description: 'Découvrez nos plus beaux chalets avec spa privé, cheminées monumentales et vues époustouflantes sur les sommets enneigés.',
      image: 'https://images.unsplash.com/photo-1551524164-6cf2ac35cd44?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
      badge: 'Hiver 2024-2025',
      destinationsCount: 15,
      villasCount: 127,
      emotion: 'intimite',
      highlights: ['Spa nordique privé', 'Cheminée design', 'Ski aux pieds', 'Chef à domicile']
    },
    {
      id: 'summer-islands',
      category: 'seasonal', 
      title: 'Été Indien Méditerranéen',
      subtitle: 'L\'élégance des îles en automne',
      description: 'Profitez de la douceur automnale dans nos villas face à la mer, loin des foules estivales mais avec toute la magie méditerranéenne.',
      image: 'https://images.unsplash.com/photo-1613395877344-13d4a8e0d49e?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
      badge: 'Septembre - Novembre',
      destinationsCount: 22,
      villasCount: 89,
      emotion: 'serenite',
      highlights: ['Température idéale', 'Tarifs préférentiels', 'Plages privées', 'Couchers de soleil']
    },
    {
      id: 'family-adventures',
      category: 'family',
      title: 'Aventures Familiales Mémorables',
      subtitle: 'Créer des souvenirs ensemble',
      description: 'Villas spacieuses avec piscines sécurisées, aires de jeux et activités pour tous les âges. Le bonheur en famille dans un cadre d\'exception.',
      image: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=2080&q=80',
      badge: 'Parfait en famille',
      destinationsCount: 18,
      villasCount: 156,
      emotion: 'anticipation',
      highlights: ['Piscines sécurisées', 'Activités enfants', 'Espaces jeux', 'Services baby-sitting']
    },
    {
      id: 'romantic-escapes',
      category: 'romantic',
      title: 'Escapades Romantiques',
      subtitle: 'L\'amour en toute intimité',
      description: 'Villas intimistes avec jacuzzis privés, dîners aux chandelles et services de concierge dédiés pour des moments à deux inoubliables.',
      image: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
      badge: 'Moments intimes',
      destinationsCount: 12,
      villasCount: 67,
      emotion: 'intimite',
      highlights: ['Jacuzzi privé', 'Dîner romantique', 'Spa couple', 'Vue panoramique']
    },
    {
      id: 'wellness-retreats',
      category: 'wellness',
      title: 'Retraites Bien-être',
      subtitle: 'Ressourcement corps et âme',
      description: 'Villas zen avec espaces méditation, spas privés et programmes wellness personnalisés pour retrouver l\'équilibre intérieur.',
      image: 'https://images.unsplash.com/photo-1582063289852-62e3ba2747f8?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
      badge: 'Bien-être absolu',
      destinationsCount: 8,
      villasCount: 43,
      emotion: 'serenite',
      highlights: ['Spa privatif', 'Yoga & méditation', 'Cuisine detox', 'Massages à domicile']
    },
    {
      id: 'adventure-luxury',
      category: 'adventure',
      title: 'Aventures de Luxe',
      subtitle: 'Adrénaline et raffinement',
      description: 'Combinez sensations fortes et confort absolu avec nos villas situées près des plus beaux spots d\'aventure au monde.',
      image: 'https://images.unsplash.com/photo-1540541338287-41700207dee6?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
      badge: 'Aventure premium',
      destinationsCount: 14,
      villasCount: 78,
      emotion: 'anticipation',
      highlights: ['Sports nautiques', 'Randonnées VIP', 'Guides privés', 'Équipements haut de gamme']
    }
  ];

  const filteredCollections = activeCategory === 'all' 
    ? inspirationCollections 
    : inspirationCollections.filter(collection => collection.category === activeCategory);

  const handleExploreCollection = (collection) => {
    navigate(`/search?inspiration=${collection.id}&emotion=${collection.emotion}`);
  };

  const handleCategoryClick = (categoryId) => {
    setActiveCategory(categoryId);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1600607687644-c7171b42498b?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60" />
        </div>
        
        <div className="relative z-10 max-w-6xl mx-auto text-center text-white">
          <Badge 
            variant="secondary" 
            className="mb-6 px-4 py-2 bg-white/10 backdrop-blur-md border-white/20 text-white text-sm font-light uppercase tracking-wider"
          >
            <Compass className="w-4 h-4 mr-2" />
            Sources d'Inspiration
          </Badge>
          
          <h1 
            className="text-5xl md:text-6xl font-light mb-6 tracking-wide"
            style={{ 
              fontFamily: 'Playfair Display, serif',
              textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
            }}
          >
            Vous ne savez pas où partir ?
          </h1>
          
          <p 
            className="text-xl md:text-2xl font-light mb-8 opacity-90 max-w-3xl mx-auto leading-relaxed"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Laissez-vous inspirer par nos collections thématiques et découvrez votre prochaine destination de rêve
          </p>
          
          <Button 
            size="lg"
            className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105 shadow-xl"
          >
            Explorer les Collections
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Categories Filter */}
      <section className="py-8 px-6 bg-white/50 backdrop-blur-sm border-y border-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap gap-3 justify-center">
            {inspirationCategories.map((category) => {
              const IconComponent = category.icon;
              const isActive = activeCategory === category.id;
              
              return (
                <Button
                  key={category.id}
                  variant={isActive ? 'default' : 'outline'}
                  onClick={() => handleCategoryClick(category.id)}
                  className={`px-6 py-3 rounded-full transition-all duration-300 ${
                    isActive 
                      ? 'bg-gradient-to-r from-amber-600 to-amber-700 text-white shadow-lg transform -translate-y-1' 
                      : 'bg-white/80 text-gray-700 border-gray-200 hover:bg-white/90 backdrop-blur-sm'
                  }`}
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: '500',
                    letterSpacing: '0.025em'
                  }}
                >
                  <IconComponent className="w-4 h-4 mr-2" />
                  {category.name}
                </Button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Collections Grid */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-light mb-6 text-gray-800"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Collections d'Inspiration
            </h2>
            <p 
              className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Chaque collection raconte une histoire, évoque une émotion et vous guide vers l'expérience parfaite
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCollections.map((collection, index) => (
              <Card 
                key={collection.id}
                className={`group cursor-pointer overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-1 ${
                  index === 0 ? 'md:col-span-2 lg:col-span-2' : ''
                }`}
                onClick={() => handleExploreCollection(collection)}
              >
                <div className={`relative ${index === 0 ? 'h-80' : 'h-72'} overflow-hidden`}>
                  <img 
                    src={collection.image}
                    alt={collection.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  
                  {/* Subtle gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  
                  {/* Minimalist badge */}
                  <div className="absolute top-4 right-4">
                    <Badge 
                      variant="secondary" 
                      className="backdrop-blur-md bg-white/10 text-white border-white/20 px-3 py-1 text-xs font-light uppercase tracking-wider"
                    >
                      {collection.villasCount} villas
                    </Badge>
                  </div>

                  {/* Minimal content overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="text-white">
                      <h3 
                        className={`font-light mb-2 tracking-wide ${
                          index === 0 ? 'text-3xl md:text-4xl' : 'text-2xl'
                        }`}
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        {collection.title}
                      </h3>
                      
                      <p 
                        className={`font-light opacity-90 mb-4 ${
                          index === 0 ? 'text-lg' : 'text-base'
                        }`}
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {collection.subtitle}
                      </p>

                      {/* Simple CTA indicator */}
                      <div className="flex items-center gap-2 text-amber-400 font-light group-hover:text-amber-300 transition-colors duration-300">
                        <span className="text-sm uppercase tracking-wide">Découvrir</span>
                        <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300" />
                      </div>
                    </div>
                  </div>

                  {/* Hover state overlay with additional info */}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="text-center text-white p-6">
                      <p 
                        className={`mb-4 leading-relaxed ${
                          index === 0 ? 'text-base max-w-lg' : 'text-sm max-w-xs'
                        }`}
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {collection.description}
                      </p>
                      
                      {/* Minimal highlights */}
                      <div className="flex flex-wrap gap-2 justify-center mb-4">
                        {collection.highlights.slice(0, 3).map((highlight, idx) => (
                          <span 
                            key={idx}
                            className="text-xs text-amber-300 font-light px-2 py-1 bg-white/10 rounded-full"
                          >
                            {highlight}
                          </span>
                        ))}
                      </div>

                      <Button 
                        size="sm"
                        className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-4 py-2 rounded-full font-light text-sm transition-all duration-300"
                      >
                        Explorer
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 px-6 bg-gradient-to-r from-amber-600 to-amber-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-3xl md:text-4xl font-light mb-6"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Votre Inspiration Personnalisée
          </h2>
          <p 
            className="text-xl mb-8 opacity-90 leading-relaxed"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Nos conseillers créent des expériences sur-mesure selon vos passions et vos rêves
          </p>
          <Button 
            size="lg"
            variant="outline"
            className="border-2 border-white text-white hover:bg-white hover:text-amber-700 px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105"
          >
            Parler à un Conseiller
            <Heart className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default InspirationPage;